import {Chess} from 'chess.js';

// DEVIATION from the plan: relative import instead of the '@' alias. This
// import is type-only (CourseChapter/CourseMove/CoursePage/VideoSegment are
// only ever used in type positions in this file), so babel would likely elide
// the runtime require anyway — but jest still has no moduleNameMapper for '@'
// (see useGeneratedCourse.ts), and jest.config.js may not be touched, so a
// relative path is used to remove any doubt.
import {
  ChapterRoleName,
  CourseChapter,
  CourseMove,
  CoursePage,
  CoursePageConfig,
  VideoSegment,
} from '../../Hooks/LmsCourseApi/types';

export type MoveOutcome =
  | {status: 'wrong'}
  | {
      status: 'reply';
      fen: string;
      /**
       * The position after the student's move but before the reply. The UI
       * shows this first and lands the reply a beat later, so the opponent
       * does not appear to move at the same instant as the student.
       */
      afterStudentFen: string;
      replyMove: string;
      studentSan: string;
      replySan: string;
      nextPly: number;
    }
  | {status: 'solved'; fen: string; studentSan: string};

export function isVideoPage(page: CoursePage) {
  return page?.content_type === 'video';
}

export function isPuzzlePage(page: CoursePage) {
  return page?.content_type === 'chess_position';
}

/**
 * A generated course points every video chapter at the same recording,
 * distinguished by this window. A page without one is not playable as a slice.
 */
export function getVideoSegment(page: CoursePage): VideoSegment | null {
  const raw = page?.config?.video_segment as {start: unknown; end: unknown} | undefined;
  if (!raw) return null;
  const start = Number(raw.start);
  const end = Number(raw.end);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  return {start, end};
}

/**
 * Every position along a line, starting with the one you are given.
 *
 * Used to step through a tutor answer on the board. Returns `n + 1` positions
 * for `n` legal moves, and stops at the first move that will not play — though
 * the server has already validated these, so that is a belt-and-braces guard
 * rather than an expected path.
 */
export function positionsAlongLine(fen: string, line: string[]): string[] {
  let game: InstanceType<typeof Chess>;
  try {
    game = new Chess(fen);
  } catch {
    return [];
  }

  const positions = [game.fen()];
  for (const move of line ?? []) {
    const uci = parseUci(move ?? '');
    if (!uci) break;
    try {
      const played = game.move({
        from: uci.from,
        to: uci.to,
        promotion: uci.promotion ?? 'q',
      });
      if (!played) break;
    } catch {
      break;
    }
    positions.push(game.fen());
  }
  return positions;
}

/**
 * Squares the piece on `square` may legally move to.
 *
 * Click-to-move is implemented here rather than left to chessboard-package:
 * its own click handling keeps the selection in props supplied by its wrapper,
 * and in this app that state never updates — clicking a piece highlights
 * nothing, so the board reads as dead even though the move itself lands.
 */
export function legalDestinations(fen: string, square: string): string[] {
  if (!/^[a-h][1-8]$/.test(square || '')) return [];
  try {
    const game = new Chess(fen);
    return game
      .moves({square: square as never, verbose: true})
      .map((m: {to: string}) => m.to);
  } catch {
    return [];
  }
}

/**
 * The square the solving piece starts on, from the scripted first move.
 *
 * Used as the "show me where" hint when the course was generated before hints
 * were stored on the page, so older courses still get one.
 */
export function firstMoveSquare(moves: CourseMove[] | null): string {
  const first = expectedMoveForPly(moves, 1);
  const uci = (first?.move ?? '').trim().toLowerCase();
  return /^[a-h][1-8]/.test(uci) ? uci.slice(0, 2) : '';
}

export function expectedMoveForPly(
  moves: CourseMove[] | null,
  ply: number,
): CourseMove | undefined {
  return (moves ?? []).find(m => m.ply === ply);
}

function parseUci(uci: string) {
  const match = uci.trim().toLowerCase().match(/^([a-h][1-8])([a-h][1-8])([nbrq])?$/);
  if (!match) return null;
  return {from: match[1], to: match[2], promotion: match[3]};
}

/**
 * The `single_variant` contract: the student plays odd plies, the engine replies
 * on even plies, and the line ends on an odd ply. A move that is not the
 * scripted one is rejected without mutating the position.
 */
export function applyStudentMove(
  fen: string,
  moves: CourseMove[] | null,
  ply: number,
  from: string,
  to: string,
  promotion?: string,
): MoveOutcome {
  const scripted = expectedMoveForPly(moves, ply);
  if (!scripted) return {status: 'wrong'};

  const expected = parseUci(scripted.move);
  if (!expected) return {status: 'wrong'};
  if (expected.from !== from || expected.to !== to) return {status: 'wrong'};

  const game = new Chess(fen);
  let played;
  try {
    played = game.move({from, to, promotion: promotion ?? expected.promotion ?? 'q'});
  } catch {
    return {status: 'wrong'};
  }
  if (!played) return {status: 'wrong'};

  const studentSan = played.san;
  const afterStudentFen = game.fen();

  const reply = expectedMoveForPly(moves, ply + 1);
  if (!reply) return {status: 'solved', fen: afterStudentFen, studentSan};

  const replyUci = parseUci(reply.move);
  let replySan = '';
  if (replyUci) {
    try {
      const replyPlayed = game.move({
        from: replyUci.from,
        to: replyUci.to,
        promotion: replyUci.promotion ?? 'q',
      });
      replySan = replyPlayed?.san ?? '';
    } catch {
      // A malformed scripted reply should not strand the student mid-puzzle.
      return {status: 'solved', fen: afterStudentFen, studentSan};
    }
  }
  return {
    status: 'reply',
    fen: game.fen(),
    afterStudentFen,
    replyMove: reply.move,
    studentSan,
    replySan,
    nextPly: ply + 2,
  };
}

export function nextChapterIndex(
  chapters: CourseChapter[],
  currentIndex: number,
): number | null {
  const next = currentIndex + 1;
  return next < (chapters?.length ?? 0) ? next : null;
}

export function isChapterComplete(
  chapter: CourseChapter,
  completedPageIds: Set<number>,
): boolean {
  const pages = chapter?.pages ?? [];
  return pages.length > 0 && pages.every(p => completedPageIds.has(p.id));
}

const ROLES: ChapterRoleName[] = ['setup', 'puzzle', 'answer', 'practice', 'video', 'wrapup'];

/**
 * What a chapter is. A pose-anchored course writes it on the page
 * (`config.role`); a course built before that is read from the page kind, so
 * it still plays chapters in order exactly as it always did.
 */
export function chapterRole(chapter?: CourseChapter | null): ChapterRoleName {
  const page = chapter?.pages?.[0];
  const role = page?.config?.role;
  if (role && ROLES.includes(role)) return role;
  return page && isPuzzlePage(page) ? 'puzzle' : 'video';
}

/** A set-up video that runs straight into its puzzle opens the puzzle itself. */
export function opensPuzzleNext(chapters: CourseChapter[], index: number): boolean {
  const next = nextChapterIndex(chapters, index);
  return (
    chapterRole(chapters[index]) === 'setup' &&
    next != null &&
    chapterRole(chapters[next]) === 'puzzle'
  );
}

/**
 * The coach's answer to the posed puzzle at `index`: the chapter right after
 * it. A practice puzzle has none — when it splits an answer in two, the video
 * after it is the rest of the earlier puzzle's answer, not its own.
 */
export function answerChapterIndex(chapters: CourseChapter[], index: number): number | null {
  if (chapterRole(chapters[index]) !== 'puzzle') return null;
  const next = nextChapterIndex(chapters, index);
  return next != null && chapterRole(chapters[next]) === 'answer' ? next : null;
}

/** What the Continue button offers. */
export function continueLabel(chapters: CourseChapter[], index: number): string {
  return answerChapterIndex(chapters, index) != null ? "Watch the coach's answer" : 'Continue';
}

/**
 * The video chapter playing `seconds` of the recording. A course assembled
 * from several uploads has one clock per part, so 2:05 of part 2 is not 2:05
 * of part 1: when the recording is known it has to match as well.
 */
export function videoChapterAt(
  chapters: CourseChapter[],
  seconds: number,
  videoCourseId?: number | null,
): number {
  return (chapters ?? []).findIndex(chapter => {
    const page = chapter?.pages?.[0];
    const segment = page ? getVideoSegment(page) : null;
    if (!segment) return false;
    const own = page?.config?.video_course_id;
    if (videoCourseId != null && own != null && own !== videoCourseId) return false;
    return seconds >= segment.start && seconds <= segment.end;
  });
}

/** How much of the set-up "See the set-up again" replays before the question ends. */
export const SETUP_REPLAY_SEC = 30;

/**
 * Where "See the set-up again" goes for the puzzle at `index`: the video
 * chapter holding the moment the coach finished asking, a little before it.
 */
export function setupReplay(
  chapters: CourseChapter[],
  index: number,
): {index: number; startAt: number} | null {
  const config = chapters?.[index]?.pages?.[0]?.config;
  const poseEnd = Number(config?.pose_end);
  if (config?.pose_end == null || !Number.isFinite(poseEnd)) return null;
  // Searched backwards from the puzzle: the set-up is the video before it,
  // and its window ends exactly where the question does.
  for (let i = index - 1; i >= 0; i--) {
    const page = chapters[i]?.pages?.[0];
    const segment = page ? getVideoSegment(page) : null;
    if (!segment) continue;
    const own = page?.config?.video_course_id;
    if (config?.video_course_id != null && own != null && own !== config.video_course_id) continue;
    if (poseEnd >= segment.start - 0.5 && poseEnd <= segment.end + 0.5) {
      return {index: i, startAt: Math.max(segment.start, poseEnd - SETUP_REPLAY_SEC)};
    }
  }
  return null;
}

/** "Mating pattern · Greco's mate", from the puzzle's analysis; '' without one. */
export function puzzleTypeLabel(config?: CoursePageConfig | null): string {
  const found = config?.analysis;
  if (!found?.type) return '';
  const type = found.type.charAt(0).toUpperCase() + found.type.slice(1);
  return found.pattern ? `${type} · ${found.pattern}` : type;
}

/** The heading a puzzle gets when its own one cannot be trusted not to spoil it. */
export const NEUTRAL_PUZZLE_HEADING = 'Find the best move';

export type PuzzleCopy = {
  heading: string;
  /** The task: "White to play and mate in 2." '' when there is none to show. */
  line: string;
  /** What kind of puzzle it is. '' until solved — it names the idea. */
  typeLabel: string;
  /** The idea's name. '' until solved. */
  answerTitle: string;
  /** The explanation. '' until solved. */
  summary: string;
};

/**
 * What the lesson card says around a puzzle, before and after it is solved.
 *
 * Nothing that names the idea may show before the student has found it. A
 * spoiler-safe page (`config.spoiler_safe`) keeps the answer in its own fields,
 * so its heading and text are safe throughout. A page generated before that
 * contract carries the idea as its heading and the solution as its text, so
 * both are held back until it is solved and a neutral heading stands in — the
 * turn badge already says whose move it is.
 */
export function puzzleCopy(page: CoursePage, solved: boolean): PuzzleCopy {
  const config = page?.config ?? ({} as CoursePageConfig);
  const safe = config.spoiler_safe === true;
  const task = (config.task?.label ?? '').trim();
  const heading = (page?.heading ?? '').trim();
  const text = (page?.text ?? '').trim();

  const copy: PuzzleCopy = {
    heading: (safe && heading) || NEUTRAL_PUZZLE_HEADING,
    line: (safe && text) || task,
    typeLabel: '',
    answerTitle: '',
    summary: '',
  };
  if (!solved) return copy;

  return {
    ...copy,
    typeLabel: puzzleTypeLabel(config),
    answerTitle:
      ((safe ? config.answer_title : heading) || config.analysis?.title || '').trim(),
    summary:
      ((safe ? config.solution_summary : text) || config.analysis?.summary || '').trim(),
  };
}


export type PuzzleHistoryEntry = {ply: number; san: string; side: 'you' | 'opponent'};

/** Where a correct student move started, so Undo can go back to it. */
export type PuzzleTurnStart = {
  fen: string;
  lastMoveFen: string;
  ply: number;
  historyLength: number;
  /** The line being followed then, and how much of it had been played. */
  line?: number;
  playedLength?: number;
};

export type PuzzleRun = {
  /**
   * idle: the student's move. replying: their move is on the board and the
   * scripted reply is due. wrong: a wrong move is held on the board with the
   * right one shown beside it, and nothing else may be played. solved: the
   * line is finished.
   */
  phase: 'idle' | 'replying' | 'wrong' | 'solved';
  start: string;
  /** The primary line. */
  moves: CourseMove[] | null;
  /**
   * Reviewer-approved alternatives: whole lines in UCI from `start`, solver
   * first. Optional so a run built by hand stays valid; `initialPuzzleRun`
   * always sets it.
   */
  alternatives?: string[][];
  /** The line being followed: 0 is the primary, n is alternatives[n - 1]. */
  line?: number;
  /** Every ply played so far on the line (from+to UCI), replies included. */
  played?: string[];
  /** The position on the line. A wrong move never changes it. */
  fen: string;
  /** The position before `fen` — the board's last-move highlight diffs the two. */
  lastMoveFen: string;
  ply: number;
  history: PuzzleHistoryEntry[];
  /** The wrong move on show, and the position it left. */
  wrong: {from: string; to: string; san: string; fen: string} | null;
  /** The move the line wanted instead. null when the script cannot be read. */
  correct: {from: string; to: string; san: string} | null;
  /** Every wrong move made, by ply, in the order made — for the scoresheet. */
  mistakes?: Record<number, string[]>;
  /** The next move, shown on request ("Show move"). Put away by any move. */
  revealed?: {from: string; to: string; san: string} | null;
  /** The reply waiting to land. */
  reply: {fen: string; san: string; nextPly: number} | null;
  /** 'good' after a correct move of the student's own that the line goes on from. */
  verdict: 'good' | null;
  /** A wrong move was made in this attempt: it ends "Solved with help". */
  helped: boolean;
  /** Solved at least once. Survives Restart — the page stays complete. */
  solvedOnce: boolean;
  turnStarts: PuzzleTurnStart[];
  /**
   * Bumped whenever a move is taken back. chessboard-package keeps its own copy
   * of the position, so a take-back only reaches the screen if the board is
   * remounted; this is its key.
   */
  board: number;
};

export type PuzzleAction =
  | {type: 'move'; from: string; to: string}
  | {type: 'replyLanded'}
  | {type: 'playCorrect'}
  | {type: 'tryAgain'}
  | {type: 'undo'}
  | {type: 'restart'}
  /** Show the next move of the followed line as an arrow. Counts as help. */
  | {type: 'showMove'}
  /** A different puzzle: nothing carries over, not even having solved it. */
  | {type: 'load'; start: string; moves: CourseMove[] | null; alternatives?: string[][] | null};

/** Only lists of UCI strings survive; anything else the backend sends is dropped. */
function cleanAlternatives(raw: unknown): string[][] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (l): l is string[] =>
      Array.isArray(l) && l.length > 0 && l.every(m => typeof m === 'string' && !!parseUci(m)),
  );
}

export function initialPuzzleRun(
  start: string,
  moves: CourseMove[] | null,
  alternatives?: string[][] | null,
): PuzzleRun {
  return {
    phase: 'idle',
    start,
    moves,
    alternatives: cleanAlternatives(alternatives),
    line: 0,
    played: [],
    mistakes: {},
    revealed: null,
    fen: start,
    lastMoveFen: start,
    ply: 1,
    history: [],
    wrong: null,
    correct: null,
    reply: null,
    verdict: null,
    helped: false,
    solvedOnce: false,
    turnStarts: [],
    board: 0,
  };
}

/** A move played from `fen`, if it is legal there. */
function tryMove(fen: string, from: string, to: string, promotion = 'q') {
  try {
    const game = new Chess(fen);
    const played = game.move({from, to, promotion});
    return played ? {san: played.san as string, fen: game.fen()} : null;
  } catch {
    return null;
  }
}

/** Line `index` as scripted moves: 0 is the primary, n is alternatives[n - 1]. */
function lineMoves(state: PuzzleRun, index: number): CourseMove[] | null {
  if (index === 0) return state.moves;
  const alt = state.alternatives?.[index - 1];
  return alt ? alt.map((move, i) => ({ply: i + 1, move})) : null;
}

const uci4 = (move: string) => move.trim().toLowerCase().slice(0, 4);

/**
 * The lines that agree with every move played so far, the one being followed
 * first — so where several would accept a move, the run stays where it is.
 */
function consistentLines(state: PuzzleRun): number[] {
  const played = state.played ?? [];
  const current = state.line ?? 0;
  const all = [0, ...(state.alternatives ?? []).map((_, i) => i + 1)];
  const agrees = (index: number) => {
    const moves = lineMoves(state, index) ?? [];
    return played.every((p, i) => uci4(expectedMoveForPly(moves, i + 1)?.move ?? '') === p);
  };
  return [current, ...all.filter(i => i !== current)].filter(agrees);
}

/** The move the followed line wants at `ply`, with its SAN from `fen`. */
function scriptedMove(state: PuzzleRun) {
  const moves = lineMoves(state, state.line ?? 0);
  const uci = parseUci(expectedMoveForPly(moves, state.ply)?.move ?? '');
  if (!uci) return null;
  const played = tryMove(state.fen, uci.from, uci.to, uci.promotion ?? 'q');
  return played ? {from: uci.from, to: uci.to, san: played.san} : null;
}

function playMove(state: PuzzleRun, from: string, to: string, own: boolean): PuzzleRun {
  // The first line that agrees with the game so far and wants this move.
  let line = state.line ?? 0;
  let outcome: MoveOutcome = {status: 'wrong'};
  for (const index of consistentLines(state)) {
    outcome = applyStudentMove(state.fen, lineMoves(state, index), state.ply, from, to);
    if (outcome.status !== 'wrong') {
      line = index;
      break;
    }
  }

  if (outcome.status === 'wrong') {
    // Only a move that can actually stand on the board can be shown on it.
    const played = tryMove(state.fen, from, to);
    if (!played) return state;
    const before = state.mistakes?.[state.ply] ?? [];
    return {
      ...state,
      phase: 'wrong',
      wrong: {from, to, san: played.san, fen: played.fen},
      correct: scriptedMove(state),
      mistakes: {
        ...state.mistakes,
        [state.ply]: before.includes(played.san) ? before : [...before, played.san],
      },
      revealed: null,
      verdict: null,
      helped: true,
    };
  }

  const turnStarts = [
    ...state.turnStarts,
    {
      fen: state.fen,
      lastMoveFen: state.lastMoveFen,
      ply: state.ply,
      historyLength: state.history.length,
      line: state.line ?? 0,
      playedLength: (state.played ?? []).length,
    },
  ];
  const played = [
    ...(state.played ?? []),
    from + to,
    ...(outcome.status === 'reply' ? [uci4(outcome.replyMove)] : []),
  ];
  const history = [
    ...state.history,
    {ply: state.ply, san: outcome.studentSan, side: 'you' as const},
  ];
  const base = {
    ...state,
    wrong: null,
    correct: null,
    revealed: null,
    turnStarts,
    history,
    line,
    played,
    lastMoveFen: state.fen,
  };

  if (outcome.status === 'reply') {
    return {
      ...base,
      phase: 'replying',
      fen: outcome.afterStudentFen,
      reply: {fen: outcome.fen, san: outcome.replySan, nextPly: outcome.nextPly},
      verdict: own ? 'good' : null,
    };
  }
  return {...base, phase: 'solved', fen: outcome.fen, verdict: null, solvedOnce: true};
}

/**
 * One attempt at a `single_variant` puzzle. Pure: the reply delay is the
 * screen's — it dispatches `replyLanded` when the beat is up.
 */
export function puzzleRunReducer(state: PuzzleRun, action: PuzzleAction): PuzzleRun {
  switch (action.type) {
    case 'move':
      if (state.phase !== 'idle') return state;
      return playMove(state, action.from, action.to, true);

    case 'replyLanded': {
      if (state.phase !== 'replying' || !state.reply) return state;
      const {reply} = state;
      return {
        ...state,
        phase: 'idle',
        lastMoveFen: state.fen,
        fen: reply.fen,
        ply: reply.nextPly,
        history: [...state.history, {ply: state.ply + 1, san: reply.san, side: 'opponent'}],
        reply: null,
      };
    }

    case 'playCorrect': {
      if (state.phase !== 'wrong' || !state.correct) return state;
      const {from, to} = state.correct;
      const cleared = {...state, phase: 'idle' as const, wrong: null, board: state.board + 1};
      return playMove(cleared, from, to, false);
    }

    case 'tryAgain':
      if (state.phase !== 'wrong') return state;
      return {...state, phase: 'idle', wrong: null, correct: null, board: state.board + 1};

    case 'showMove': {
      if (state.phase !== 'idle') return state;
      const next = scriptedMove(state);
      if (!next) return state;
      return {...state, revealed: next, helped: true};
    }

    case 'undo': {
      const last = state.turnStarts[state.turnStarts.length - 1];
      if (state.phase === 'wrong' || !last) return state;
      return {
        ...state,
        phase: 'idle',
        fen: last.fen,
        lastMoveFen: last.lastMoveFen,
        ply: last.ply,
        history: state.history.slice(0, last.historyLength),
        turnStarts: state.turnStarts.slice(0, -1),
        line: last.line ?? 0,
        played: (state.played ?? []).slice(0, last.playedLength ?? 0),
        // The plies being rewound are played again from scratch; what went
        // wrong on them goes with them. `helped` stays — the help was had.
        mistakes: Object.fromEntries(
          Object.entries(state.mistakes ?? {}).filter(([ply]) => Number(ply) < last.ply),
        ),
        revealed: null,
        reply: null,
        verdict: null,
        board: state.board + 1,
      };
    }

    case 'restart':
      return {
        ...initialPuzzleRun(state.start, state.moves, state.alternatives),
        solvedOnce: state.solvedOnce,
        board: state.board + 1,
      };

    case 'load':
      return {
        ...initialPuzzleRun(action.start, action.moves, action.alternatives),
        board: state.board + 1,
      };

    default:
      return state;
  }
}

/** What the board shows for a run: the wrong move played, the right one as an arrow. */
export function puzzleBoardView(state: PuzzleRun): {
  position: string;
  lastMoveFen: string;
  arrow: [string, string] | null;
} {
  if (state.phase === 'wrong' && state.wrong) {
    return {
      position: state.wrong.fen,
      lastMoveFen: state.fen,
      arrow: state.correct ? [state.correct.from, state.correct.to] : null,
    };
  }
  const shown = state.phase === 'idle' ? state.revealed : null;
  return {
    position: state.fen,
    lastMoveFen: state.lastMoveFen,
    arrow: shown ? [shown.from, shown.to] : null,
  };
}

export type ScoreCell = {
  ply: number;
  /** The move that stands on the line — the right one. */
  san: string;
  /** Wrong moves tried at this ply, in the order tried. */
  wrong: string[];
  /** The latest move on the board. */
  current: boolean;
};

export type ScoreRow = {number: number; white: ScoreCell | null; black: ScoreCell | null};

/**
 * The run as a scoresheet: move number, White, Black, numbered from 1 for the
 * puzzle — "1… e5" when Black is to move. A ply that went wrong keeps its
 * mistakes beside the right move; while a wrong move is on the board its ply
 * already shows the right one, as the board does.
 */
export function scoresheet(state: PuzzleRun): ScoreRow[] {
  const blackFirst = (state.start.split(' ')[1] ?? 'w') === 'b';
  const cells: ScoreCell[] = state.history.map(h => ({
    ply: h.ply,
    san: h.san,
    wrong: state.mistakes?.[h.ply] ?? [],
    current: false,
  }));
  if (state.phase === 'wrong' && state.wrong) {
    cells.push({
      ply: state.ply,
      san: state.correct?.san ?? '',
      wrong: state.mistakes?.[state.ply] ?? [state.wrong.san],
      current: false,
    });
  }
  if (cells.length) cells[cells.length - 1].current = true;

  const rows: ScoreRow[] = [];
  for (const c of cells) {
    const index = c.ply - 1 + (blackFirst ? 1 : 0);
    const number = Math.floor(index / 2) + 1;
    let row = rows.find(r => r.number === number);
    if (!row) {
      row = {number, white: null, black: null};
      rows.push(row);
    }
    if (index % 2 === 0) row.white = c;
    else row.black = c;
  }
  return rows;
}

/**
 * Free play, for analysis after the solve: any legal move for the side to
 * move, judged by no one. The new FEN, or null when the move is not legal.
 */
export function playFreeMove(
  fen: string,
  from: string,
  to: string,
  promotion = 'q',
): string | null {
  return tryMove(fen, from, to, promotion)?.fen ?? null;
}

/**
 * The caption under a video, or undefined when it would only repeat the
 * chapter title already printed above the player. An answer video keeps its
 * caption: that is the idea's name, which the neutral chapter title holds back.
 */
export function videoCaption(
  heading: string | null | undefined,
  chapterTitle: string | null | undefined,
): string | undefined {
  const caption = (heading ?? '').trim();
  if (!caption) return undefined;
  const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
  return norm(caption) === norm(chapterTitle ?? '') ? undefined : caption;
}
